import React from 'react';
import * as S from './styles';
import { IconCreditCard } from '@veneer/core';
import { useTranslationHelper } from 'src/contexts/dependencyManager/utils';
import { PaymentMethodTexts } from './types';
import { usePaymentMethods } from 'src/hooks/usePaymentMethods/usePaymentMethods';
import useShellContext from 'src/hooks/useShellContext/useShellContext';
import LoadingHandler from '../LoadingHandler/LoadingHandler';
import { CardError } from '../CardError/CardError';
import {
  getCardImage,
  maskCardNumber,
  formatExpiryDate
} from '../../utils/payment';

export type PaymentMethodProps = {
  paymentMethodId?: string;
  showChangeButton?: boolean;
};

export const PaymentMethod: React.FC<PaymentMethodProps> = ({
  paymentMethodId,
  showChangeButton = true
}) => {
  const getText = useTranslationHelper();
  const { authProvider, stack, navigation } = useShellContext();

  const { paymentMethods, loading, error } = usePaymentMethods({
    authProvider,
    stack,
    paymentMethodId
  });

  const texts: PaymentMethodTexts = {
    paymentMethodTitle: getText('paymentMethod.title', 'Payment method'),
    changePaymentText: getText('paymentMethod.changePayment', 'Change payment'),
    cardAltText: getText('cardAltText', 'Card logo')
  };

  const handleChangePayment = (
    e:
      | React.MouseEvent<HTMLAnchorElement>
      | React.KeyboardEvent<HTMLAnchorElement>
  ) => {
    e.preventDefault();
    navigation?.push('/account-details/shipping-billing');
  };

  const paymentDetails = paymentMethods?.payment_method_details;
  const cardType = paymentDetails?.card_type;
  const cardImage = getCardImage(cardType);
  const maskedCardNumber = maskCardNumber(paymentDetails?.last_four);
  const formattedExpiryDate = formatExpiryDate(paymentDetails?.expiration_date);

  return (
    <S.PaymentMethodCard>
      <LoadingHandler
        loading={loading}
        error={Boolean(error)}
        $position="absolute"
        loadingText={getText('loading', 'Loading...')}
        customError={
          <CardError
            forceHeight={false}
            icon={<IconCreditCard size={32} />}
            texts={[
              <>
                <p>
                  {getText(
                    'paymentMethod.error.title.paragraph1',
                    'Something went wrong.'
                  )}
                </p>
                <p>
                  {getText(
                    'paymentMethod.error.title.paragraph2',
                    'Your payment method information did not load.'
                  )}
                </p>
              </>,
              getText(
                'paymentMethod.error.description',
                'Please refresh the page.'
              )
            ]}
          />
        }
      >
        <S.CardContent>
          <S.CardHeader>
            <IconCreditCard size={32} />
            <S.CardTitle>{texts.paymentMethodTitle}</S.CardTitle>
          </S.CardHeader>

          <S.PaymentDetailsContainer>
            <S.CardImg
              src={cardImage}
              alt={texts.cardAltText}
            />
            <S.UserCardDetails>
              <S.UserName>
                {paymentDetails?.name_on_card || 'Cardholder Name'}
              </S.UserName>
              <S.CardNumber>{maskedCardNumber}</S.CardNumber>
              <S.ExpiryDate>{formattedExpiryDate}</S.ExpiryDate>
            </S.UserCardDetails>
          </S.PaymentDetailsContainer>

          {showChangeButton && !loading && !error && (
            <S.ChangePaymentLink
              href="#"
              onClick={handleChangePayment}
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleChangePayment(event);
                }
              }}
            >
              {texts.changePaymentText}
            </S.ChangePaymentLink>
          )}
        </S.CardContent>
      </LoadingHandler>
    </S.PaymentMethodCard>
  );
};
